import { useEffect, useState, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Music, Clock, CheckCircle, Loader2, Users,
  Package, Archive, Eye, ChevronRight, Search, Filter, BarChart3,
  AlertTriangle, Zap, Ticket, FileText, Star, ShieldBan, UserSearch
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { useAuth } from "@/hooks/useAuth";
import { useAdmin } from "@/hooks/useAdmin";
import { supabase } from "@/integrations/supabase/client";
import AdminInvitePanel from "@/components/admin/AdminInvitePanel";
import type { Tables } from "@/integrations/supabase/types";

type SongOrder = Tables<"song_orders">;

const eventTypeLabels: Record<string, string> = {
  wedding: "Wedding", birthday: "Birthday", anniversary: "Anniversary",
  graduation: "Graduation", memorial: "Memorial", proposal: "Proposal",
  baby_shower: "Baby Shower", retirement: "Retirement", other: "Other",
};

const statusLabels: Record<string, string> = {
  submitted: "Submitted", in_review: "In Review", drafting_lyrics: "Writing Lyrics",
  composing: "Composing", ready_for_review: "Ready for Review",
  revision_requested: "Changes Requested", approved: "Approved", completed: "Completed",
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { isAdmin, loading: adminLoading } = useAdmin();
  const [orders, setOrders] = useState<SongOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [eventFilter, setEventFilter] = useState<string>("all");
  const [showTeam, setShowTeam] = useState(false);

  useEffect(() => {
    if (!authLoading && !adminLoading) {
      if (!user) navigate("/login");
      else if (!isAdmin) navigate("/dashboard");
    }
  }, [user, isAdmin, authLoading, adminLoading, navigate]);

  useEffect(() => {
    const fetchOrders = async () => {
      if (!user || !isAdmin) return;
      const { data, error } = await supabase
        .from("song_orders")
        .select("*")
        .neq("status", "pending_payment")
        .order("created_at", { ascending: false });
      if (!error && data) setOrders(data);
      setLoading(false);
    };
    if (isAdmin) fetchOrders();
  }, [user, isAdmin]);

  const filteredOrders = useMemo(() => {
    return orders.filter((o) => {
      const matchesSearch =
        !searchQuery ||
        o.recipient_names.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (o.order_number && String(o.order_number).includes(searchQuery));
      const matchesStatus = statusFilter === "all" || o.status === statusFilter;
      const matchesEvent = eventFilter === "all" || o.event_type === eventFilter;
      return matchesSearch && matchesStatus && matchesEvent;
    });
  }, [orders, searchQuery, statusFilter, eventFilter]);

  // Sort active orders by urgency: rush first, then by event date proximity
  const activeOrders = useMemo(() => {
    const active = filteredOrders.filter(o => o.status !== "completed");
    return active.sort((a, b) => {
      if (a.rush_delivery && !b.rush_delivery) return -1;
      if (!a.rush_delivery && b.rush_delivery) return 1;
      const aDate = a.event_date ? new Date(a.event_date).getTime() : Infinity;
      const bDate = b.event_date ? new Date(b.event_date).getTime() : Infinity;
      return aDate - bDate;
    });
  }, [filteredOrders]);
  const completedOrders = useMemo(() => filteredOrders.filter(o => o.status === "completed"), [filteredOrders]);

  if (authLoading || adminLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }


  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-24 pb-16">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
            <div>
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-2">
                Admin Dashboard
              </h1>
              <p className="text-muted-foreground">Manage all song orders and team members.</p>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            {[
              { label: "Total Orders", value: orders.length, icon: Package, color: "text-primary" },
              { label: "Active", value: orders.filter(o => o.status !== "completed").length, icon: Clock, color: "text-accent-foreground" },
              { label: "Completed", value: orders.filter(o => o.status === "completed").length, icon: CheckCircle, color: "text-primary" },
              { label: "Awaiting Review", value: orders.filter(o => o.status === "ready_for_review").length, icon: Eye, color: "text-accent-foreground" },
            ].map((stat, i) => (
              <motion.div key={stat.label} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                className="bg-card rounded-xl p-6 shadow-soft">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <stat.icon className={`w-6 h-6 ${stat.color}`} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-foreground">{stat.value}</p>
                    <p className="text-muted-foreground text-sm">{stat.label}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Quick Links */}
          <div className="flex flex-wrap gap-3 mb-8">
            <Button variant="outline" onClick={() => navigate("/admin/sales")}>
              <BarChart3 className="w-4 h-4 mr-2" /> Sales
            </Button>
            <Button variant="outline" onClick={() => navigate("/admin/coupons")}>
              <Ticket className="w-4 h-4 mr-2" /> Coupons
            </Button>
            <Button variant="outline" onClick={() => setShowTeam(v => !v)}>
              <Users className="w-4 h-4 mr-2" /> Team
            </Button>
            <Button variant="outline" onClick={() => navigate("/admin/blog")}>
              <FileText className="w-4 h-4 mr-2" /> Blog
            </Button>
            <Button variant="outline" onClick={() => navigate("/admin/reviews")}>
              <Star className="w-4 h-4 mr-2" /> Reviews
            </Button>
            <Button variant="outline" onClick={() => navigate("/admin/customers")}>
              <ShieldBan className="w-4 h-4 mr-2" /> Customers
            </Button>
            <Button variant="outline" onClick={() => navigate("/admin/user-test")}>
              <UserSearch className="w-4 h-4 mr-2" /> User Testing
            </Button>
          </div>

          {showTeam && (
            <div className="mb-8">
              <AdminInvitePanel />
            </div>
          )}

          {/* Search & Filters */}
          <div className="flex flex-col md:flex-row gap-3 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by name or order #..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                aria-label="Search orders by name or order number"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-48" aria-label="Filter by status">
                <Filter className="w-4 h-4 mr-2 text-muted-foreground" />
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {Object.entries(statusLabels).map(([val, label]) => (
                  <SelectItem key={val} value={val}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={eventFilter} onValueChange={setEventFilter}>
              <SelectTrigger className="w-full md:w-48" aria-label="Filter by event type">
                <SelectValue placeholder="Event type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Events</SelectItem>
                {Object.entries(eventTypeLabels).map(([val, label]) => (
                  <SelectItem key={val} value={val}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Tabs defaultValue="active" className="space-y-6">
            <TabsList>
              <TabsTrigger value="active">Active Orders ({activeOrders.length})</TabsTrigger>
              <TabsTrigger value="completed">Completed ({completedOrders.length})</TabsTrigger>
            </TabsList>

            <TabsContent value="active">
              <OrderList orders={activeOrders} loading={loading} />
            </TabsContent>
            <TabsContent value="completed">
              <OrderList orders={completedOrders} loading={loading} />
            </TabsContent>
          </Tabs>
        </div>
      </main>
      <Footer />
    </div>
  );
};

const getUrgencyInfo = (order: SongOrder) => {
  const now = new Date();
  const badges: { label: string; className: string; icon: React.ReactNode }[] = [];
  if (order.rush_delivery) {
    badges.push({ label: "Rush", className: "bg-destructive/15 text-destructive border-destructive/30", icon: <Zap className="w-3 h-3" /> });
  }
  if (order.event_date) {
    const eventDate = new Date(order.event_date);
    const daysUntil = Math.ceil((eventDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    if (daysUntil < 0) {
      badges.push({ label: "Past event", className: "bg-muted text-muted-foreground border-border", icon: <AlertTriangle className="w-3 h-3" /> });
    } else if (daysUntil <= 3) {
      badges.push({ label: `${daysUntil}d left`, className: "bg-destructive/15 text-destructive border-destructive/30", icon: <AlertTriangle className="w-3 h-3" /> });
    } else if (daysUntil <= 7) {
      badges.push({ label: `${daysUntil}d left`, className: "bg-orange-500/15 text-orange-600 border-orange-500/30", icon: <Clock className="w-3 h-3" /> });
    }
  }
  return badges;
};

const OrderList = ({ orders, loading }: { orders: SongOrder[]; loading: boolean }) => {
  if (loading) {
    return <div className="p-12 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }
  if (orders.length === 0) {
    return (
      <div className="bg-card rounded-2xl shadow-soft p-12 text-center">
        <Archive className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
        <p className="text-muted-foreground">No orders match your filters.</p>
      </div>
    );
  }
  return (
    <div className="bg-card rounded-2xl shadow-soft overflow-hidden divide-y divide-border">
      {orders.map((order, i) => {
        const badges = getUrgencyInfo(order);
        return (
          <motion.div key={order.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.03 }}>
            <Link to={`/admin/order/${order.id}`} className="flex items-center justify-between p-5 hover:bg-muted/30 transition-colors">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Music className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold text-foreground">
                      <span className="text-muted-foreground font-mono text-sm mr-2">#{order.order_number}</span>
                      {eventTypeLabels[order.event_type]} — {order.recipient_names}
                    </p>
                    {badges.map((badge, bi) => (
                      <span key={bi} className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full border ${badge.className}`}>
                        {badge.icon} {badge.label}
                      </span>
                    ))}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {order.plan_type.charAt(0).toUpperCase() + order.plan_type.slice(1)} · {statusLabels[order.status] || order.status}
                    {order.event_date && <span className="ml-2">· Event: {new Date(order.event_date).toLocaleDateString()}</span>}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm text-muted-foreground hidden sm:inline">
                  {new Date(order.created_at).toLocaleDateString()}
                </span>
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
              </div>
            </Link>
          </motion.div>
        );
      })}
    </div>
  );
};

export default AdminDashboard;
